import type { AttributionEconomics, MilestoneKey, UaBreakdown, UaBreakdownBy, UaOverview } from '@growth/client';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ColumnChart, LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { AttributionBadge, ATTRIBUTION, noteEs } from '../components/ua.tsx';
import { Card, ChartCard, DataTable, Empty, ErrorBox, StatTile } from '../components/ui.tsx';
import { describeFilters, useGlobalFilters } from '../filters.ts';
import { fmtAgo, fmtCompact, fmtDay, fmtInt, fmtMoney, fmtMoneyRange, fmtNum, fmtPct } from '../format.ts';
import { useApi } from '../session.tsx';

const MILESTONES: { key: MilestoneKey; label: string }[] = [
  { key: 'first_open', label: 'Primera apertura' },
  { key: 'tutorial_start', label: 'Empieza el tutorial' },
  { key: 'tutorial_complete', label: 'Completa el tutorial' },
  { key: 'first_run', label: 'Primera partida' },
  { key: 'second_run', label: 'Segunda partida' },
  { key: 'd1_retained', label: 'Retenido D1' },
  { key: 'd7_retained', label: 'Retenido D7' },
  { key: 'rewarded_ad_user', label: 'Ve un rewarded' },
  { key: 'first_ad_revenue', label: 'Genera ingresos por anuncios' },
  { key: 'payer', label: 'Pagador' },
];

const BREAKDOWNS: { by: UaBreakdownBy; label: string }[] = [
  { by: 'country', label: 'País' },
  { by: 'ad_group', label: 'Grupo de anuncios' },
  { by: 'asset', label: 'Recurso (asset)' },
];

/** Connection state + data freshness, shown under the page title. */
export function CoverageLine({ coverage }: { coverage: UaOverview['coverage'] | undefined }) {
  if (!coverage) return null;
  const c = coverage.connections;
  const failing = c.filter((x) => x.enabled && x.status === 'error').length;
  return (
    <span>
      Google Ads hasta {coverage.ads_through ? fmtDay(coverage.ads_through) : '—'} · Play hasta {coverage.play_through ? fmtDay(coverage.play_through) : '—'} · última
      sincronización {fmtAgo(c.map((x) => x.last_success_at).filter(Boolean).sort().at(-1) ?? null)}
      {failing ? ` · ${failing} conexión(es) con error` : ''}
    </span>
  );
}

function EconomicsCard({ modes, currency }: { modes: AttributionEconomics[]; currency: string }) {
  const cell = (m: AttributionEconomics, k: MilestoneKey) => {
    const x = m.milestones[k];
    if (!x || x.cost === null) return '—';
    const range = k === 'd1_retained' || k === 'd7_retained' ? ` (${fmtMoneyRange(x.cost_low, x.cost_high, currency)})` : '';
    return `${fmtMoney(x.cost, currency, 2)}${range} · ${fmtInt(x.count)}`;
  };
  return (
    <Card
      title="Coste por hito del juego"
      subtitle="Gasto ÷ jugadores que llegan al hito. D1/D7: coste por primera apertura ÷ retención de las instalaciones maduras (entre paréntesis, IC 95 %)."
    >
      <DataTable
        data={{
          columns: [{ label: 'Hito' }, ...modes.map((m) => ({ label: ATTRIBUTION[m.mode]?.label ?? m.mode, numeric: true }))],
          rows: [
            ['Jugadores en la cohorte', ...modes.map((m) => fmtInt(m.matched_installs ?? m.cohort_installs))],
            ['Gasto atribuido', ...modes.map((m) => fmtMoney(m.spend, currency))],
            ...MILESTONES.map((ms) => [ms.label, ...modes.map((m) => cell(m, ms.key))]),
          ],
        }}
      />
      <div className="stack small muted" style={{ marginTop: 8 }}>
        {modes.map((m) => (
          <span key={m.mode}>
            <AttributionBadge kind={m.mode} /> {ATTRIBUTION[m.mode]?.help ?? m.explanation}
            {m.mode === 'direct' && m.coverage !== null && m.coverage !== undefined ? ` Cobertura: ${fmtPct(m.coverage)} de las instalaciones de la red.` : ''}
          </span>
        ))}
      </div>
    </Card>
  );
}

function BreakdownCard({ gameId, filtersKey, params, currency }: { gameId: string; filtersKey: string; params: Record<string, unknown>; currency: string }) {
  const [by, setBy] = useState<UaBreakdownBy>('country');
  const r = useApi<UaBreakdown>(`ua-bd:${gameId}:${by}:${filtersKey}`, (api, s) => api.uaBreakdown(gameId, by, params, s));
  const name = (row: Record<string, unknown>) =>
    by === 'country' ? String(row.country ?? '—') : by === 'asset' ? `${row.name ?? row.youtube_title ?? row.asset_id} · ${String(row.field_type ?? '').toLowerCase()}` : String(row.name ?? row.ad_group_id ?? '—');
  return (
    <Card
      title="Desglose"
      subtitle={by === 'asset' ? 'Reparto de Google dentro de las campañas de aplicaciones: direccional.' : 'Datos de la red por día y nivel; los niveles se solapan y nunca se suman entre sí.'}
      actions={
        <div className="segmented" role="group" aria-label="Desglose">
          {BREAKDOWNS.map((b) => (
            <button key={b.by} type="button" aria-pressed={by === b.by} onClick={() => setBy(b.by)}>
              {b.label}
            </button>
          ))}
        </div>
      }
    >
      {r.error && <ErrorBox error={r.error} onRetry={r.reload} />}
      <div className={`loading-frame${r.loading && r.data ? ' stale' : ''}`}>
        {r.data && r.data.rows.length === 0 ? (
          <Empty>Sin datos de la red en este periodo.</Empty>
        ) : (
          <DataTable
            data={{
              columns: [
                { label: BREAKDOWNS.find((b) => b.by === by)!.label },
                { label: 'Gasto', numeric: true },
                { label: 'Impresiones', numeric: true },
                { label: 'Clics', numeric: true },
                { label: 'Instalaciones', numeric: true },
                { label: 'CTR', numeric: true },
                { label: 'IPM', numeric: true },
                { label: 'CPI', numeric: true },
                ...(by === 'asset' ? [{ label: 'Creatividad' }] : []),
              ],
              rows: (r.data?.rows ?? []).map((row) => [
                name(row),
                fmtMoney(row.spend, currency),
                fmtInt(row.impressions),
                fmtInt(row.clicks),
                fmtNum(row.installs, 1),
                fmtPct(row.ctr, 2),
                fmtNum(row.ipm, 2),
                fmtMoney(row.cpi, currency, 3),
                ...(by === 'asset'
                  ? [row.creative_id ? <Link to={`/g/${gameId}/creatives/${encodeURIComponent(String(row.creative_id))}`}>{String(row.creative_id)}</Link> : <span className="muted">sin vincular</span>]
                  : []),
              ]),
            }}
          />
        )}
      </div>
    </Card>
  );
}

export function AcquisitionPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const o = useApi<UaOverview>(`ua:${gameId}:${filters.key}`, (api, s) => api.uaOverview(gameId, filters.params, s));
  const d = o.data;
  const t = d?.totals;
  const ccy = d?.currency ?? 'EUR';
  const stale = o.loading && !!d;
  const noConnections = d && d.coverage.connections.length === 0;
  const cmp = d?.installs_comparison;

  return (
    <>
      <PageHeader title="Adquisición" sub={<CoverageLine coverage={d?.coverage} />} />
      <FilterBar filters={filters} compare={false} environment={false} />
      {o.error && <ErrorBox error={o.error} onRetry={o.reload} />}
      {noConnections && (
        <div className="notice">
          Aún no hay conexiones. Añade Google Ads y Google Play Console en{' '}
          <Link to={`/g/${gameId}/settings/connections`}>Configuración ▸ Conexiones</Link>: son de solo lectura y las credenciales se quedan en el servidor.
        </div>
      )}
      {d?.notes.map((n) => (
        <div key={n} className="notice">
          {noteEs(n)}
        </div>
      ))}
      {t && t.missing_fx > 0 && <div className="notice">Hay gasto sin tipo de cambio del BCE todavía: el total en {ccy} está incompleto (job sync-fx).</div>}

      <div className={`kpis loading-frame${stale ? ' stale' : ''}`}>
        <StatTile label="Gasto" value={fmtMoney(t?.spend, ccy)} sub={d ? `${fmtDay(d.filters.from)} – ${fmtDay(d.filters.to)}` : undefined} trend={d?.daily.map((x) => x.spend ?? 0)} />
        <StatTile label="Instalaciones (red)" value={fmtCompact(t?.installs)} sub="conversiones de instalación" trend={d?.daily.map((x) => x.installs ?? 0)} />
        <StatTile label="CPI" value={fmtMoney(t?.cpi, ccy, 3)} sub={t?.cpi_ci ? `IC 95 % ${fmtMoneyRange(t.cpi_ci.low, t.cpi_ci.high, ccy)}` : undefined} />
        <StatTile label="CTR" value={fmtPct(t?.ctr, 2)} sub={t?.ctr_ci.low !== undefined && t?.ctr !== null ? `IC 95 % ${fmtPct(t?.ctr_ci.low, 2)}–${fmtPct(t?.ctr_ci.high, 2)}` : undefined} />
        <StatTile label="IPM" value={fmtNum(t?.ipm, 2)} sub="instalaciones por 1.000 impresiones" />
        <StatTile label="CVR" value={fmtPct(t?.cvr)} sub="instalaciones / clics" />
        <StatTile label="CPM" value={fmtMoney(t?.cpm, ccy)} sub={`CPC ${fmtMoney(t?.cpc, ccy, 3)}`} />
        <StatTile label="Impresiones" value={fmtCompact(t?.impressions)} sub={`${fmtCompact(t?.clicks)} clics`} />
      </div>

      <div className="grid cols-2">
        <ChartCard
          title="Gasto por día"
          subtitle={`En ${ccy} (tipos de cambio del BCE)${describeFilters(filters.params) ? ` · ${describeFilters(filters.params)}` : ''}`}
          stale={stale}
          table={{
            columns: [{ label: 'Día' }, { label: 'Gasto', numeric: true }, { label: 'Impresiones', numeric: true }, { label: 'Clics', numeric: true }, { label: 'Instalaciones', numeric: true }, { label: 'CPI', numeric: true }],
            rows: (d?.daily ?? []).map((x) => [x.date, fmtMoney(x.spend, ccy), fmtInt(x.impressions), fmtInt(x.clicks), fmtNum(x.installs, 1), fmtMoney(x.cpi, ccy, 3)]),
          }}
        >
          <ColumnChart
            ariaLabel="Gasto por día"
            x={(d?.daily ?? []).map((x) => x.date)}
            formatX={(v) => fmtDay(String(v))}
            formatY={(v) => fmtMoney(v, ccy, 0)}
            formatValue={(v) => fmtMoney(v, ccy)}
            series={[{ key: 'spend', label: 'Gasto', slot: 1, values: (d?.daily ?? []).map((x) => x.spend ?? 0) }]}
          />
        </ChartCard>
        <ChartCard
          title="Tres formas de contar instalaciones"
          subtitle="Google Ads (día del clic), Play (instaladores nuevos, todas las fuentes) y primera apertura vista por el SDK (Android). Nunca coinciden exactamente."
          stale={stale}
          table={
            cmp && {
              columns: [{ label: 'Día' }, { label: 'Google Ads', numeric: true }, { label: 'Play: nuevos', numeric: true }, { label: 'Play: ficha', numeric: true }, { label: 'Juego', numeric: true }, { label: 'Juego (Google Ads)', numeric: true }],
              rows: cmp.daily.map((x) => [x.date, fmtNum(x.ads_installs, 1), fmtInt(x.play_new_users), fmtInt(x.play_store_acquisitions), fmtInt(x.game_first_opens), fmtInt(x.game_first_opens_google_ads)]),
            }
          }
        >
          {cmp && (
            <LineChart
              ariaLabel="Instalaciones según Google Ads, Play y el juego"
              x={cmp.daily.map((x) => x.date)}
              formatX={(v) => fmtDay(String(v))}
              formatY={fmtCompact}
              formatValue={(v) => fmtNum(v, 1)}
              series={[
                { key: 'ads', label: 'Google Ads', slot: 1, values: cmp.daily.map((x) => x.ads_installs) },
                { key: 'play', label: 'Play: instaladores nuevos', slot: 2, values: cmp.daily.map((x) => x.play_new_users) },
                { key: 'game', label: 'Juego: primera apertura', slot: 3, values: cmp.daily.map((x) => x.game_first_opens) },
              ]}
            />
          )}
          {cmp && (
            <p className="small muted" style={{ margin: '6px 0 0' }}>
              Totales: Google Ads {fmtNum(cmp.totals.ads_installs, 0)} · Play {fmtInt(cmp.totals.play_new_users)} · juego {fmtInt(cmp.totals.game_first_opens)} ({fmtInt(cmp.totals.game_first_opens_google_ads)} atribuidas a
              Google Ads). Faltan en el juego quienes rechazan el consentimiento de analítica o nunca abren la app.
            </p>
          )}
        </ChartCard>
      </div>

      <Card title="Campañas" subtitle="Nivel campaña: gasto y conversiones tal como los informa la red (atribución compartida).">
        <div className={`loading-frame${stale ? ' stale' : ''}`}>
          <DataTable
            data={{
              columns: [
                { label: 'Campaña' },
                { label: 'Estado' },
                { label: 'Gasto', numeric: true },
                { label: '% gasto', numeric: true },
                { label: 'Instalaciones', numeric: true },
                { label: 'CTR', numeric: true },
                { label: 'IPM', numeric: true },
                { label: 'CVR', numeric: true },
                { label: 'CPI', numeric: true },
              ],
              rows: (d?.campaigns ?? []).map((c) => [
                <span title={`${c.provider} · ${c.campaign_id} · ${c.channel_type ?? ''} ${c.channel_subtype ?? ''}`}>{c.name ?? c.campaign_id}</span>,
                c.status?.toLowerCase() ?? '—',
                fmtMoney(c.spend, ccy),
                fmtPct(c.share_of_spend, 0),
                fmtNum(c.installs, 1),
                fmtPct(c.ctr, 2),
                fmtNum(c.ipm, 2),
                fmtPct(c.cvr),
                fmtMoney(c.cpi, ccy, 3),
              ]),
            }}
          />
        </div>
      </Card>

      {d && d.economics.length > 0 && <EconomicsCard modes={d.economics} currency={ccy} />}
      <BreakdownCard gameId={gameId} filtersKey={filters.key} params={filters.params as Record<string, unknown>} currency={ccy} />
    </>
  );
}
